import { ChangeEvent, FocusEvent, useEffect, useMemo, useState } from "react"
import classNames from "classnames"
import { isEqual } from "lodash"

import { useLazyGetContractorsQuery } from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"

import { getAddressLabelsByCountry } from "utils/addressUtils"
import { convertDataToOptions } from "utils/convertData"
import {
  enterpriseWebsiteValidationMessage,
  getEnterpriseWebsiteValidationError,
  isEnterpriseWebsiteValid,
  normalizeEnterpriseWebsiteUrl,
} from "utils/enterpriseWebsite"
import { phoneNumberValidationMessage, phonePattern } from "utils/phoneNumber"
import { AddressFields } from "modals/components/contractorModalContent/AddressFields"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { InputLogo } from "components/form/InputLogo"
import { InputNew } from "components/form/InputNew"
import { InputPhone } from "components/form/InputPhone"
import { SelectDynamic } from "components/form/SelectDynamic"
import { ToggleButton } from "components/form/ToggleButton"

import styles from "./index.module.scss"

interface Props {
  account: UITypes.EnterpriseAccount
  onSubmit: (account: UITypes.EnterpriseAccount) => void
}

const validationInitialState = {
  field: "",
  message: "",
}

const enterpriseEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const requiredSelectFields: Array<"country" | "zip" | "state" | "city"> = ["country", "zip", "state", "city"]

export const EnterpriseAccountModalContent = ({ account, onSubmit }: Props) => {
  const [inputValues, setInputValues] = useState<UITypes.EnterpriseAccount>(account)
  const [error, setError] = useState(validationInitialState)
  const currentZip = useAppSelector(state => state.tempData.currentZip)
  const addressLabels = getAddressLabelsByCountry(inputValues.country?.[0]?.label)

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.currentTarget

    setInputValues(prevState => ({ ...prevState, [name]: type === "checkbox" ? checked : value }))

    if (name === "url" && (value.trim() === "" || isEnterpriseWebsiteValid(value))) {
      setError(prevState => (prevState.field === "url" ? validationInitialState : prevState))
    }
  }

  const onInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget

    if (name !== "url") return

    const validationError = getEnterpriseWebsiteValidationError(value)

    if (validationError) {
      setError({ field: "url", message: validationError })
      return
    }

    setError(prevState => (prevState.field === "url" ? validationInitialState : prevState))

    if (value.trim()) {
      setInputValues(prevState => ({
        ...prevState,
        [name]: normalizeEnterpriseWebsiteUrl(value),
      }))
    }
  }

  const validate = () => {
    if (!inputValues.name.trim()) {
      setError({ field: "name", message: "Company name is required" })
      return false
    }

    if (!inputValues.address1.trim()) {
      setError({ field: "address1", message: "Address 1 is required" })
      return false
    }

    const missingRequiredSelect = requiredSelectFields.find(field => !inputValues[field]?.length)

    if (missingRequiredSelect) {
      const fieldLabelMap: Record<string, string> = {
        country: "Country",
        zip: addressLabels.zip,
        state: addressLabels.state,
        city: "City",
      }

      setError({ field: missingRequiredSelect, message: `${fieldLabelMap[missingRequiredSelect]} is required` })
      return false
    }

    if (!inputValues.email.trim()) {
      setError({ field: "email", message: "Email is required" })
      return false
    }

    if (!enterpriseEmailPattern.test(inputValues.email)) {
      setError({ field: "email", message: "This email is not valid. Please check for spelling errors and try again." })
      return false
    }

    const urlError = getEnterpriseWebsiteValidationError(inputValues.url)

    if (urlError) {
      setError({ field: "url", message: enterpriseWebsiteValidationMessage })
      return false
    }

    if (inputValues.phone && !phonePattern.test(inputValues.phone)) {
      setError({ field: "phone", message: phoneNumberValidationMessage })
      return false
    }

    setError(validationInitialState)
    return true
  }

  const isFormReady = useMemo(() => {
    const hasRequiredFields =
      inputValues.name.trim() &&
      inputValues.address1.trim() &&
      requiredSelectFields.every(field => Boolean(inputValues[field]?.length)) &&
      inputValues.email.trim()
    if (!hasRequiredFields) return false
    if (!enterpriseEmailPattern.test(inputValues.email)) return false
    if (getEnterpriseWebsiteValidationError(inputValues.url)) return false
    if (inputValues.phone && !phonePattern.test(inputValues.phone)) return false
    return true
  }, [inputValues])

  const isEditMode = Boolean(account.id)
  const hasChanges = useMemo(() => !isEqual(inputValues, account), [account, inputValues])
  const canSubmit = isFormReady && (!isEditMode || hasChanges)

  const handleSubmit = () => {
    if (!validate()) return

    onSubmit(inputValues)
  }

  useEffect(() => {
    if (currentZip?.city) {
      const city = convertDataToOptions({ data: [currentZip.city] }, "id", "name")
      const state = convertDataToOptions({ data: [currentZip.state] }, "id", "name")
      const country = convertDataToOptions({ data: [currentZip.country] }, "id", "name")
      const suburb = convertDataToOptions(
        { data: currentZip.suburbs?.length > 0 ? [currentZip.suburbs[0]] : null },
        "id",
        "name",
      )

      if (city && state) {
        setInputValues(prevState => ({
          ...prevState,
          city,
          state,
          country,
          suburb,
        }))
      }
    }
  }, [currentZip])

  return (
    <>
      <div
        className={classNames(styles.saveButton, { [styles.saveButtonDisabled]: !canSubmit })}
        role="button"
        aria-disabled={!canSubmit}
        onClick={canSubmit ? handleSubmit : undefined}
      >
        Save
      </div>
      <div className={styles.enterpriseContainer}>
        <ModalSectionTitle title="Details" />
        <div className={styles.row}>
          <InputNew
            label="Company name *"
            placeholder="Enter company name"
            name="name"
            value={inputValues.name}
            className={styles.inputText}
            onChange={onInputChange}
            errorMessage={error.field === "name" ? error.message : ""}
          />
        </div>
        <AddressFields
          inputValues={inputValues}
          error={error}
          onInputChange={onInputChange}
          setInputValues={setInputValues}
        />
        <div className={styles.row}>
          <div className={styles.toggleFieldWrapper}>
            <ToggleButton
              id="isEnterpriseDisabled"
              name="isDisable"
              isChecked={inputValues.isDisable}
              onChange={onInputChange}
            >
              Disable Account
            </ToggleButton>
          </div>
        </div>

        <ModalSectionTitle title="Affiliated Contractors" />
        <div className={styles.row}>
          <SelectDynamic
            label="Connect contractors"
            placeholder="Search"
            searchFieldName="name"
            valueName="id"
            labelName="name"
            name="linkedContractors"
            values={inputValues.linkedContractors || []}
            className={styles.inputText}
            useLazyQuery={useLazyGetContractorsQuery}
            onChange={value => setInputValues({ ...inputValues, linkedContractors: value })}
            queryParams={{ isEnabled: 1 }}
            requestOnSearchOnly
            multi
            backspaceDelete
          />
        </div>

        <ModalSectionTitle title="Additional Information" />
        <div className={styles.row}>
          <InputNew
            label="Email *"
            placeholder="Enter email address"
            name="email"
            value={inputValues.email}
            className={styles.inputText}
            onChange={onInputChange}
            errorMessage={error.field === "email" ? error.message : ""}
          />
        </div>
        <div className={styles.row}>
          <InputNew
            label="Enterprise web URL (optional)"
            placeholder="Enter URL"
            name="url"
            value={inputValues.url}
            className={styles.inputText}
            onChange={onInputChange}
            onBlur={onInputBlur}
            errorMessage={error.field === "url" ? error.message : ""}
          />
        </div>
        <div className={styles.row}>
          <InputNew
            label="Full name (optional)"
            placeholder="Enter full name"
            name="fullName"
            value={inputValues.fullName}
            className={styles.inputText}
            onChange={onInputChange}
          />
        </div>
        <div className={styles.row}>
          <InputPhone
            label="Phone number (optional)"
            placeholder="Enter phone"
            name="phone"
            value={inputValues.phone}
            className={styles.inputText}
            onChange={value => setInputValues({ ...inputValues, phone: value })}
            errorMessage={error.field === "phone" ? error.message : ""}
          />
        </div>
        <div className={styles.row}>
          <InputLogo
            name="enterpriseLogo"
            id="enterpriseLogo"
            value={inputValues.image && [inputValues.image]}
            imageUrl={inputValues.logo}
            errorMessage={error.field === "logo" ? error.message : ""}
            label="Enterprise logo (optional)"
            className={styles.enterpriseLogo}
            imageWrapperClassName={styles.enterpriseLogoImageWrapper}
            fileWrapperClassName={styles.enterpriseLogoFileWrapper}
            formatNote="(Format: Png Ratio: 2:1 Max size: 750x375px)"
            onSetFile={files =>
              setInputValues(prevState => {
                setError({ field: "logo", message: "" })
                return { ...prevState, image: files?.[0] }
              })
            }
            onValidationError={errorMessage => {
              setError({ field: "logo", message: errorMessage })
            }}
          />
        </div>
      </div>
    </>
  )
}
